import React, { useEffect, useRef, useState } from 'react';
import { Box, FormHelperText, Typography, styled, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormikProps, useFormik } from 'formik';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import toast from 'react-hot-toast';
import CustomMultiSelect from '../CustomMultiSelect/CustomMultiSelect';
import { useAppSelector } from '../../redux/hooks';
import {
  IClient,
  IClientObjective,
  IUpdateClientCoachAssignmentInput,
  IUpdateClientObjectivesInput,
} from '../../redux/client/clientSlice';
import { clientObjectives, ClientType } from '../../types/client';
import { getInitials } from '../../utils/userInputUtils';
import { graphLineColors } from '../../utils/styles';
import {
  initialValues,
  FormValues,
  StaffMember,
  clientDetailsFormValidatorSchema,
} from '../../utils/validations/clientDetailsForm';
import StyledButton from '../Common/StyledButton';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';
import { ICoachGroup, IGetCoachGroupPayload } from '../../redux/coachGroup/coachGroupSlice';
import { GET_COACH_GROUP } from '../../service/graphql/coachGroupQuery';
import {
  GET_CLIENT_BY_ID,
  UPDATE_CLIENT_COACH_ASSIGNMENT,
  UPDATE_CLIENT_OBJECTIVES_MUTATION,
} from '../../service/graphql/clientsQuery';
import { toastStyles } from '../../utils/utils';
import { ICoach } from '../../redux/coach/coachSlice';
import GroupClientDetailForm from './GroupClientDetailForm';
import SingleClientDetailForm from './SingleClientDetailForm';

export const StyledFormHelperText = styled(FormHelperText)({
  color: '#FF0000 !important',
  marginLeft: '0px',
  marginTop: '4px',
  fontSize: '12px',
});

const ImageContainer = styled(Box)({
  position: 'relative',
  width: '100px',
  height: '100px',
});

interface ClientDetailFormProps {
  clientId: string;
}

function ClientDetailForm({ clientId }: ClientDetailFormProps) {
  const [getCoachGroup, { loading: getGroupLoading, data: coachGroup }] = useLazyQuery<
    { getCoachGroup: ICoachGroup },
    { getCoachGroupInput: IGetCoachGroupPayload }
  >(GET_COACH_GROUP, {
    fetchPolicy: 'network-only',
  });
  const {
    loading: clientLoading,
    data: selectedClient,
    error: clientLoadingError,
  } = useQuery<{ getClientById: IClient }>(GET_CLIENT_BY_ID, {
    variables: { id: clientId },
    fetchPolicy: 'network-only',
  });
  const [
    updateClientObjectives,
    { error: updateObjectivesError, data: updateObjectivesData, loading: updateObjectivesLoading },
  ] = useMutation<unknown, IUpdateClientObjectivesInput>(UPDATE_CLIENT_OBJECTIVES_MUTATION);
  const [
    updateClientAssignment,
    { error: updateAssignmentError, data: updateAssignmentData, loading: updateAssignmentLoading },
  ] = useMutation<unknown, IUpdateClientCoachAssignmentInput>(UPDATE_CLIENT_COACH_ASSIGNMENT);

  const coach = useAppSelector((state) => state.coach.coach);
  const clientCoach = selectedClient?.getClientById?.coach;
  const coachId = coach?.id || '';
  const [tags, setTags] = useState<string[]>([]);
  const [isFormValid, setIsFormValid] = useState<string | boolean | null | undefined>(true);
  const [tagError, setTagError] = useState<string | null>(null);
  const { t } = useTranslation();
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;
  const objectivesInputRef = useRef<HTMLInputElement>(null);
  const fixedOptions = [
    { id: coach.id, name: t('common.me'), email: coach.email, profilePic: coach.profilePic },
  ];

  if (clientCoach?.id !== coachId && coachId) {
    fixedOptions.push({
      id: clientCoach?.id || '',
      name: `${clientCoach?.firstName} ${clientCoach?.lastName}`,
      email: clientCoach?.email || '',
      profilePic: clientCoach?.profilePic || '',
    });
  }

  const [options, setOptions] = useState([...fixedOptions]);
  const [currentSkip, setCurrentSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loadMoreOptions = () => {
    if (getGroupLoading || !hasMore) return;
    getCoachGroup({
      variables: { getCoachGroupInput: { skip: currentSkip, take: 4, isactiveCoaches: true } },
    });
  };

  useEffect(() => {
    if (updateObjectivesError) toast.success(updateObjectivesError.message, toastStyles.error);
    if (updateAssignmentError) toast.success(updateAssignmentError.message, toastStyles.error);
  }, [updateObjectivesError, updateAssignmentError]);

  useEffect(() => {
    if (!updateObjectivesData || !updateAssignmentData) return;
    toast.success(t('addNewClient.toastMessage.clientUpdated'), toastStyles.success);
  }, [updateObjectivesData, updateAssignmentData]);

  const handleUpdateClientObjectives = (
    firstName: string,
    lastName?: string,
    description?: string,
    coaches?: string[]
  ) => {
    if (!selectedClient) return;

    let objectives: IClientObjective[] | null;
    if (tags && tags.length > 0) {
      objectives = tags.map((tag, index) => ({
        objectiveName: tag,
        colorCode: graphLineColors[index],
      }));
    } else if (objectivesInputRef.current?.value.trim()) {
      objectives = [
        {
          objectiveName: objectivesInputRef.current?.value.trim(),
          colorCode: graphLineColors[0],
        },
      ];
    } else {
      objectives = null;
    }

    const payload: IUpdateClientObjectivesInput = {
      UpdateClientObjectives: {
        coachId: selectedClient.getClientById.coach?.id || coachId,
        clientId: selectedClient.getClientById.id,
        objectives,
        firstName,
        description,
        lastName,
        coaches,
        coachGroupId: coachGroup?.getCoachGroup?.id,
      },
    };
    updateClientObjectives({ variables: payload });
    updateClientAssignment({
      variables: { clientId: selectedClient.getClientById.id, coachIds: coaches || [] },
    });
  };

  const formik: FormikProps<FormValues> = useFormik({
    initialValues,
    validationSchema: clientDetailsFormValidatorSchema,
    onSubmit: () =>
      handleUpdateClientObjectives(
        formik.values.firstName,
        formik.values.lastName,
        formik.values.description,
        formik.values.staffMembers?.filter((e) => e.id !== clientCoach?.id).map((c) => c.id)
      ),
  });
  //  to check form validation

  const validateForm = () =>
    ((tags.length > 0 && tags.length <= 6) || objectivesInputRef.current?.value.trim()) &&
    formik.values.firstName.length > 0;

  useEffect(() => {
    setIsFormValid(validateForm());
  }, [tags, tagError, objectivesInputRef, formik.values]);

  const triggerTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setTagError(null);
    if (e.target.value) {
      setIsFormValid(true);
    } else {
      setIsFormValid(validateForm());
    }
  };

  useEffect(() => {
    if (!coachGroup?.getCoachGroup?.coaches) return;
    let coaches = coachGroup?.getCoachGroup?.coaches;
    if (!coach.isPrincipalCoach) {
      coaches = coaches.filter((c) => c.id !== coachId);
    }

    if (coaches.length > 0) {
      setCurrentSkip((prev) => prev + 4);
      const mappedCoaches = coaches.map((c) => ({
        id: c.id,
        name: `${c.firstName} ${c.lastName}`,
        email: c.email,
        profilePic: c.profilePic,
      }));
      setOptions((prevOptions) => [...prevOptions, ...mappedCoaches]);
    }
  }, [coachGroup?.getCoachGroup?.coaches]);

  useEffect(() => {
    if (!coachGroup?.getCoachGroup?.totalCount) return;
    const totalCount = coachGroup?.getCoachGroup?.totalCount;
    if (totalCount > 0 && totalCount + 1 <= options.length) {
      setHasMore(false);
    }
  }, [coachGroup?.getCoachGroup?.totalCount, options]);

  useEffect(() => {
    if (!selectedClient?.getClientById) return;
    const clientData = selectedClient?.getClientById;
    formik.resetForm();

    const staff: ICoach[] = [];
    if (clientData.coach) {
      staff.push(clientData.coach);
    }
    if (Array.isArray(clientData.teamMembers)) {
      staff.push(...clientData.teamMembers);
    }

    const updatedInitialValues: FormValues = {
      firstName: clientData.firstName,
      lastName: clientData.lastName,
      description: clientData.description,
      dob: clientData.dob,
      pronoun: clientData.pronoun,
      email: clientData.email,
      phoneNumber: clientData.tp,
      objectives: clientData.objectives?.map((objective) => objective.objectiveName) || [],
      staffMembers: staff.map((member) => {
        let name = `${member.firstName} ${member.lastName}`;
        if (member.id === coachId) {
          name = t('common.me');
        }
        return {
          id: member.id,
          name,
          email: member.email,
          profilePic: member.profilePic,
        };
      }),
    };
    formik.setValues(updatedInitialValues);
    setTags(clientData.objectives?.map((objective) => objective.objectiveName) || []);
    getCoachGroup({
      variables: { getCoachGroupInput: { skip: 0, take: 4, isactiveCoaches: true } },
    });
    setOptions([...fixedOptions]);
  }, [selectedClient, formik.setValues]);

  const handleTagsChange = (newValue: (string | object)[]) => {
    const uniqueTags = Array.from(new Set(newValue.map((tag) => (tag as string).trim())));
    const isTagTooLong = uniqueTags.some((tag) => tag.length > 50);

    if (isTagTooLong) {
      setTagError(t('addNewClient.isTagTooLong'));
      return;
    }
    if (uniqueTags.length !== newValue.length) {
      setTagError(t('addNewClient.duplicate'));
      return;
    }
    if (uniqueTags.length < 1) {
      setTags(uniqueTags);
      setTagError(t('addNewClient.empty'));
    } else if (uniqueTags.length > 6) {
      setTagError(t('addNewClient.max'));
    } else {
      setTagError(null);
      setTags(uniqueTags);
    }
  };

  const initials = getInitials(formik.values.firstName, formik.values.lastName);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      const inputValue = (event.target as HTMLInputElement).value.trim();
      if (inputValue) {
        const isTagTooLong = inputValue.length > 50;
        if (isTagTooLong) {
          setTagError(t('addNewClient.isTagTooLong'));
          return;
        }
        if (tags.includes(inputValue)) {
          setTagError(t('addNewClient.duplicate'));
        } else if (tags.length >= 6) {
          setTagError(t('addNewClient.max'));
        } else {
          setTagError(null);
          setTags([...tags, inputValue]);
          formik.values.objectives = [...tags, inputValue];
        }
      } else if (tags.length < 1) {
        setTagError(t('addNewClient.empty'));
      }
    }
  };

  const handleStaffTagsChange = (newValue: (string | object)[]) => {
    formik.setFieldTouched('staffMembers', true);
    const idsNew = newValue.map((v) => (typeof v === 'object' && 'id' in v ? v.id : ''));
    if (
      formik.values.staffMembers &&
      newValue.length < formik.values.staffMembers.length &&
      idsNew.includes(fixedOptions[0].id)
    ) {
      formik.setFieldValue('staffMembers', newValue);
    } else {
      const ids = formik.values.staffMembers?.map((v) => v.id);
      const uniqueValues = [...newValue.filter((e) => !ids?.includes((e as StaffMember).id))];
      formik.setFieldValue('staffMembers', [
        ...(formik.values.staffMembers || []),
        ...uniqueValues,
      ]);
    }
  };

  return (
    <Grid
      gap="36px"
      p="24px"
      borderRadius="5px"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      <form onSubmit={formik.handleSubmit} noValidate>
        <Box bgcolor="#fff">
          <Box>
            <ImageContainer mb="24px">
              <Box
                bgcolor="primary.light"
                height="100%"
                borderRadius="50%"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {selectedClient?.getClientById?.profilePic ? (
                  <img
                    src={`${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${selectedClient.getClientById?.profilePic}`}
                    alt="user-avatar"
                    loading="lazy"
                    style={{
                      width: '100%',
                      objectFit: 'cover',
                      height: '100%',
                      borderRadius: '50%',
                    }}
                  />
                ) : (
                  <Typography
                    variant="h3"
                    textAlign="center"
                    color="primary.main"
                    style={{ opacity: 0.3, letterSpacing: '-0.1em' }}
                  >
                    {initials}
                  </Typography>
                )}
              </Box>
            </ImageContainer>
            <Grid container direction="column" gap="12px">
              {selectedClient?.getClientById?.clientType === ClientType.GROUP ? (
                <GroupClientDetailForm formik={formik} />
              ) : (
                <SingleClientDetailForm formik={formik} />
              )}
              <Grid item>
                {/* Objectives */}
                <CustomMultiSelect
                  onTextFieldChange={triggerTextChange}
                  options={tags.length < 6 ? clientObjectives : []}
                  value={tags}
                  onChange={handleTagsChange}
                  onKeyDown={handleKeyDown}
                  required
                  label={t('addNewClient.objectives')}
                  error={!!tagError}
                  disabled={
                    !checkPermissions(UserPermissionType.CORE_MODULE_UPDATE_CLIENT_OBJECTIVES)
                  }
                  inputRef={objectivesInputRef}
                  freeSolo={!(tags && tags.length > 0)}
                />
                {tagError && (
                  <StyledFormHelperText style={{ color: 'red', marginTop: 0 }}>
                    {tagError}
                  </StyledFormHelperText>
                )}
              </Grid>
              <Grid item>
                <Grid item xs={12}>
                  <CustomMultiSelect
                    options={options}
                    value={formik.values.staffMembers}
                    onChange={handleStaffTagsChange}
                    loadMoreOptions={loadMoreOptions}
                    label={t('addNewClient.staffMembers')}
                    freeSolo={false}
                    fixedOptions={fixedOptions || []}
                    withIcon
                  />
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Box>
        {/* Action buttons */}
        {checkPermissions(UserPermissionType.CORE_MODULE_UPDATE_CLIENT_OBJECTIVES) && (
          <Box>
            <Box display="flex" flexDirection="row-reverse" mt="20px">
              <StyledButton
                paddingX={44.5}
                disabled={
                  !!clientLoadingError || !isFormValid || tagError != null || !formik.isValid
                }
                paddingY={16.5}
                loading={clientLoading || updateObjectivesLoading || updateAssignmentLoading}
              >
                {t('addNewClient.updateData')}
              </StyledButton>
            </Box>
          </Box>
        )}
      </form>
    </Grid>
  );
}

export default ClientDetailForm;
